import type * as go from 'gojs';
import { v4 as uuidv4 } from 'uuid';
import { store } from 'createStore';
import type { AddUnitDataInterface } from 'store/units/sagaActions';
import { addUnit, editUnitInfo, removeUnits } from 'store/units/sagaActions';
import { UnitTypes } from 'unitData/types';
import type { UnitInfoFreeTextBoxInterface } from '../../store/units/types';
import type { PrintPropertiesInterface } from 'store/settings/types';
import { editPrintProperties } from 'store/settings/reducer';
import type { UndoFromPortInterface, UndoToPortInterface } from './types';
import { getNodePasteLocation } from '../utils/getPasteLocation';
import { checkIfUnitExist, checkIfUnitIsACopy } from '../utils/unitChecks';

type PasteLocations = Record<string, { x: number; y: number }>;

const getUnitInfoData = (node: go.ObjectData) => {
  const unitInfoData = store.getState().unitInfoDataState.unitInfoData;
  let currentUnitInfoData = {};
  Object.entries(unitInfoData).forEach((obj) => {
    if (node.category.includes('TRACKED')) {
      const newCategoryString = node.category.replace('_TRACKED', '');
      if (obj[0] === newCategoryString) {
        currentUnitInfoData = obj[1];
      }
    } else if (node.category.includes('WHEELED')) {
      const newCategoryString = node.category.replace('_WHEELED', '');
      if (obj[0] === newCategoryString) {
        currentUnitInfoData = obj[1];
      }
    } else {
      if (obj[0] === node.category) {
        currentUnitInfoData = obj[1];
      }
    }
  });
  return currentUnitInfoData;
};

const handleCopiedNodeInsertion = (
  node: go.ObjectData,
  pasteLocations: PasteLocations,
  newFromPortKeysMap: Map<string, UndoFromPortInterface[]>,
  newToPortKeysMap: Map<string, UndoToPortInterface[]>,
) => {
  if (!node.layout) {
    return;
  }
  const newFromPortKeys: UndoFromPortInterface[] = [];
  const newToPortKeys: UndoToPortInterface[] = [];
  for (let i = 0; i < node.layout.inputPorts; i++) {
    newToPortKeys.push({
      unitKey: node.key,
      key: uuidv4(),
    });
  }

  newToPortKeysMap.set(node.key, newToPortKeys);

  for (let i = 0; i < node.layout.outputPorts; i++) {
    newFromPortKeys.push({
      unitKey: node.key,
      key: uuidv4(),
    });
  }
  newFromPortKeysMap.set(node.key, newFromPortKeys);

  const newLayout = {
    ...node.layout,
    loc: getNodePasteLocation(node, pasteLocations),
  };
  if (node.category === UnitTypes.FEED) {
    const unitInfoData = store.getState().unitInfoDataState.unitInfoData;

    const feedUnitInfoData = unitInfoData.FEED;

    store.dispatch(
      addUnit(
        newLayout,
        feedUnitInfoData as AddUnitDataInterface,
        node.key,
        newFromPortKeys,
        newToPortKeys,
        node.unitInfo,
        node.inputFlow,
        node.flowProperties,
        node.panels,
      ),
    );
  } else {
    const unitInfoData = getUnitInfoData(node);

    store.dispatch(
      addUnit(
        newLayout,
        unitInfoData,
        node.key,
        newFromPortKeys,
        newToPortKeys,
        node.unitInfo,
        undefined,
        node.flowProperties,
        node.panels,
      ),
    );
  }
};

const handleNewNodeInsertion = (
  node: go.ObjectData,
  pasteLocations: PasteLocations,
) => {
  if (node.isPanel) {
    return;
  }
  const newLayout = {
    ...node.layout,
    loc: getNodePasteLocation(node, pasteLocations),
  };
  const unitInfoData = getUnitInfoData(node);

  store.dispatch(
    addUnit(
      newLayout,
      unitInfoData,
      node.key,
      node.outputPortArray,
      node.inputPortArray,
      node.unitInfo,
      node.inputFlow,
      undefined,
      node.panels,
    ),
  );
};

export const handleNodeModifications = (modifiedNodeData: go.ObjectData[]) => {
  modifiedNodeData.forEach((node: go.ObjectData) => {
    const units = store.getState().unitConnGroup.present.unitState.units;
    const unit = units.find((unit) => unit.key === node.key);

    if (
      typeof unit === 'undefined' ||
      typeof node.width === 'undefined' ||
      typeof node.height === 'undefined'
    ) {
      return;
    }

    if (node.category === UnitTypes.FREE_TEXT_BOX) {
      const freeTextBoxUnitInfo: UnitInfoFreeTextBoxInterface =
        unit.unitInfo as UnitInfoFreeTextBoxInterface;
      const newFreeTextBoxUnitInfo: UnitInfoFreeTextBoxInterface = {
        ...freeTextBoxUnitInfo,
        width: node.width,
        height: node.height,
      };
      store.dispatch(editUnitInfo(node.key, newFreeTextBoxUnitInfo));
    } else if (node.category === UnitTypes.PRINT_WINDOW) {
      const printProperties: PrintPropertiesInterface =
        store.getState().settingsState.printProperties;
      const newPrintProperties: PrintPropertiesInterface = {
        ...printProperties,
        width: node.width,
        height: node.height,
      };
      store.dispatch(editPrintProperties(newPrintProperties));
    }
  });
};

export const handleNodeInsertions = (
  insertedNodeKeys: go.Key[],
  modifiedNodeMap: Map<go.Key, go.ObjectData>,
  pasteLocations: PasteLocations,
  newFromPortKeysMap: Map<string, UndoFromPortInterface[]>,
  newToPortKeysMap: Map<string, UndoToPortInterface[]>,
) => {
  insertedNodeKeys.forEach((nodeKey: go.Key) => {
    const node = modifiedNodeMap.get(nodeKey);
    if (nodeKey?.toString()) {
      const currentNodeKey = nodeKey.toString();

      if (node?.isPanel || checkIfUnitExist(currentNodeKey)) {
        return;
      }

      if (checkIfUnitIsACopy(currentNodeKey) && node) {
        handleCopiedNodeInsertion(
          node,
          pasteLocations,
          newFromPortKeysMap,
          newToPortKeysMap,
        );
      } else if (node) {
        handleNewNodeInsertion(node, pasteLocations);
      }
    }
  });
};

export const handleNodeRemovals = (removedNodeKeys: (go.Key | undefined)[]) => {
  const units = store.getState().unitConnGroup.present.unitState.units;
  const unitKeys = new Set(
    units
      .map((unit) => unit.key)
      .filter((key): key is string => typeof key === 'string'),
  );
  const keys = removedNodeKeys
    .map((key) => key?.toString())
    .filter(
      (key): key is string => typeof key === 'string' && unitKeys.has(key),
    );

  if (keys.length > 0) {
    store.dispatch(removeUnits(keys));
  }
};
